import React, { useState, useEffect } from 'react';
import { Row, Col, InputNumber, Form, Button, Modal, Input, Select, Icon } from 'antd';
import styles from './PricingTableModal.module.css'; // Import custom CSS for styling

const { confirm } = Modal;

const MIN_COL_WIDTH = 300; // px — minimum width per tier column
const LABEL_COL_WIDTH = 280; // px — fixed width for the product/package column

const PricingTableModal = ({ form, productData, selectedPricingTable, onProductsChange, chargedByFunctionsOnly }) => {
  const { getFieldDecorator } = form;

  const [row, setRow] = useState([0]); // Initialize with one product row
  const [column, setColumn] = useState([0]);
  const [columnCount, setColumnCount] = useState(1); // now just tracks column.length
  const [showEmptyBox, setEmptyBox] = useState(true);
  const [counter, setCounter] = useState(1);

  const [selectedProducts, setSelectedProducts] = useState([]);

  const { setFieldsValue, resetFields, getFieldValue } = form;

  const findProductName = productId => {
    const product = productData.find(p => p._id === productId);
    return product ? product.name : '';
  };

  useEffect(() => {
    //console.log(selectedPricingTable)
    if (selectedPricingTable) {
      const pricingCriteriaKeys = Object.keys(selectedPricingTable.pricingCriteria);
      setRow(selectedPricingTable.products.map((_, index) => index));
      setColumn(pricingCriteriaKeys.map((_, index) => index));
      setColumnCount(pricingCriteriaKeys.length);
      setCounter(selectedPricingTable.products.length);
      setEmptyBox(pricingCriteriaKeys.length === 0);

      const initialValues = {
        pricingTableName: selectedPricingTable.name,
        product: selectedPricingTable.products.map((p, index) => ({
          //need to do it like this coz labelInValue
          [`product[${index}][0]`]: { key: p.product, label: findProductName(p.product) }
        })),
        header: pricingCriteriaKeys.map((key, index) => ({
          [`header[${index}].minUnit`]: selectedPricingTable.pricingCriteria[key].minUnit,
          [`header[${index}].maxUnit`]:
            selectedPricingTable.pricingCriteria[key].maxUnit === null ? null : selectedPricingTable.pricingCriteria[key].maxUnit
        })),
        monthPrice: selectedPricingTable.products.map((p, productIndex) =>
          Object.values(p.tiers).map((tier, tierIndex) => ({
            [`monthPrice[${productIndex}][${tierIndex}]`]: tier.monthPrice
          }))
        ),
        yearPrice: selectedPricingTable.products.map((
          p,
          productIndex //ProductIndex = row
        ) =>
          Object.values(p.tiers).map((tier, tierIndex) => ({
            //tierIndex = col
            [`yearPrice[${productIndex}][${tierIndex}]`]: tier.yearPrice
          }))
        )
      };

      // Flatten the initialValues object for setFieldsValue
      const flattenedInitialValues = Object.assign(
        {},
        { pricingTableName: initialValues.pricingTableName },
        ...initialValues.product,
        ...initialValues.header,
        ...initialValues.monthPrice.flat(),
        ...initialValues.yearPrice.flat()
      );

      setSelectedProducts(
        selectedPricingTable.products.map((p, index) => ({
          key: p.product,
          label: findProductName(p.product),
          fieldIndex: index
        }))
      );
      setTimeout(() => {
        setFieldsValue(flattenedInitialValues);
        notifyProductsChange();
      }, 0);
    } else {
      resetFields();
      setRow([0]);
      setColumn([0]);
      setColumnCount(3);
      setCounter(1);
      setEmptyBox(true);
      setSelectedProducts([]);

      if (onProductsChange) {
        onProductsChange([]);
      }
    }
  }, [selectedPricingTable]);

  // When chargedByFunctionsOnly is toggled on, trim to only the first product row
  useEffect(() => {
    if (chargedByFunctionsOnly && row.length > 1) {
      const rowsToRemove = row.slice(1); // all rows except the first
      const currentMonthPrice = getFieldValue('monthPrice');
      const currentYearPrice = getFieldValue('yearPrice');
      const currentProduct = getFieldValue('product');

      if (currentMonthPrice && currentYearPrice && currentProduct) {
        // Keep only the first row's data
        const updatedMonthPrice = currentMonthPrice.map(col => [col[0]]);
        const updatedYearPrice = currentYearPrice.map(col => [col[0]]);
        const updatedProduct = currentProduct.map((item, i) => (i === 0 ? item : null));

        form.setFieldsValue({
          monthPrice: updatedMonthPrice,
          yearPrice: updatedYearPrice,
          product: updatedProduct
        });
      }

      setRow([row[0]]);
      setSelectedProducts(prev => prev.filter(item => item.fieldIndex === row[0]));
      setTimeout(() => notifyProductsChange(), 0);
    }
  }, [chargedByFunctionsOnly]);

  const addProduct = () => {
    setRow([...row, counter]); // Add a new product row
    setCounter(counter + 1); // Increment counter after adding the row
  };

  const addTier = () => {
    setColumn(prev => [...prev, prev.length]);
    setColumnCount(prev => prev + 1);
    setEmptyBox(false);
  };

  const handleRemoveBox = index => {
    const { getFieldValue, setFieldsValue } = form;

    // Get field values
    const headerValues = getFieldValue('header');
    const currentMonthPrice = getFieldValue('monthPrice');
    const currentYearPrice = getFieldValue('yearPrice');

    //modify field value
    const newHeaderValues = headerValues.filter((_, i) => i !== index);
    const updatedMonthPrice = currentMonthPrice.map(col => col.filter((_, i) => i !== index));
    const updatedYearPrice = currentYearPrice.map(col => col.filter((_, i) => i !== index));

    // Update the form fields with the new monthPrice and header values
    setFieldsValue({
      monthPrice: updatedMonthPrice,
      yearPrice: updatedYearPrice,
      header: newHeaderValues
    });

    // Update column state
    const newColumn = column.filter((_, i) => i !== index);
    setColumn(newColumn);
    setColumnCount(newColumn.length);

    // Update empty box state
    setEmptyBox(newColumn.length === 0);

    if (index === column.length - 1 && index > 0) {
      const newLastColumnIndex = index - 1;
      form.setFieldsValue({
        [`header[${newLastColumnIndex}].maxUnit`]: null
      });
    }
  };

  const handleRemoveRow = productIndex => {
    const currentMonthPrice = getFieldValue('monthPrice');
    const currentYearPrice = getFieldValue('yearPrice');
    const currentProduct = getFieldValue('product');

    // Modify values
    const updatedMonthPrice = currentMonthPrice.map(col => col.filter((_, i) => i !== productIndex));
    const updatedYearPrice = currentYearPrice.map(col => col.filter((_, i) => i !== productIndex));
    const updatedProduct = currentProduct.map((item, i) => (i === productIndex ? null : item));

    //why this wont work but the above one work (filter and map ?????)
    // I think is because that the above one still keep the item in array even though its null, not removing it completely !
    //const updatedProduct = currentProduct.filter((_, i) => i !== productIndex);

    form.setFieldsValue({
      monthPrice: updatedMonthPrice,
      yearPrice: updatedYearPrice,
      product: updatedProduct
    });

    // Update rows and selected products state
    setRow(prevRow => prevRow.filter((_, index) => _ !== productIndex));
    setSelectedProducts(prevSelected => prevSelected.filter(item => item.fieldIndex !== productIndex));

    setTimeout(() => notifyProductsChange(), 0);

    /* Check and update the state after the form fields are updated
    setTimeout(() => {
      const updatedFields = form.getFieldsValue();
      console.log(updatedFields.product);
    }, 0); */
  };

  const pricing = (row, col) => {
    return (
      <div style={{ padding: '4px 8px', width: '100%' }}>
        <Input.Group compact style={{ display: 'flex', gap: '4px' }}>
          <Form.Item key={`monthPrice[${col}][${row}]`} style={{ flex: 1, marginBottom: 0 }}>
            {getFieldDecorator(`monthPrice[${col}][${row}]`, {
              rules: [
                { required: true, message: 'Please input price per month' },
                { pattern: /^(?:\d*\.\d{1,2}|\d+)$/, message: 'Please enter a valid price with up to 2 decimal places' }
              ]
            })(<Input placeholder="0.00" style={{ width: '100%' }} min={0} step={0.01} precision={2} addonAfter="/month" />)}
          </Form.Item>
          <Form.Item key={`yearPrice[${col}][${row}]`} style={{ flex: 1, marginBottom: 0 }}>
            {getFieldDecorator(`yearPrice[${col}][${row}]`, {
              rules: [
                { required: true, message: 'Please input price per year' },
                { pattern: /^(?:\d*\.\d{1,2}|\d+)$/, message: 'Please enter a valid price with up to 2 decimal places' }
              ]
            })(
              <Input
                placeholder={`${
                  getFieldValue(`monthPrice[${col}][${row}]`)
                    ? (parseFloat(getFieldValue(`monthPrice[${col}][${row}]`) || 0) * 12).toFixed(2)
                    : '0.00'
                }`}
                style={{ width: '100%' }}
                min={0}
                step={0.01}
                precision={2}
                addonAfter="/year"
              />
            )}
          </Form.Item>
        </Input.Group>
      </div>
    );
  };

  const notifyProductsChange = () => {
    const productValues = form.getFieldValue('product') || [];
    const validProducts = productValues.filter(p => p && p[0] && p[0].key).map(p => p[0]); // Extract {key, label} objects

    if (onProductsChange) {
      onProductsChange(validProducts);
    }
  };

  const handleProductSelection = (value, productIndex) => {
    //keep track of index and also value
    form.setFieldsValue({
      [`product[${productIndex}][0]`]: value
    });

    setSelectedProducts(prevSelected => {
      // Remove the previously selected product for this index, if any
      //if productIndex matches then discard the array in that specific index, to make way for new value
      const filteredSelected = prevSelected.filter(item => item.fieldIndex !== productIndex);

      // Add the new selection with its field index
      if (value) {
        return [...filteredSelected, { ...value, fieldIndex: productIndex }];
      }

      return filteredSelected;
    });
    setTimeout(() => notifyProductsChange(), 0);
  };

  const addTierButton = () => {
    return (
      <Button onClick={addTier} type="dashed" style={{ width: '100%' }}>
        <Icon type="plus" /> Add Tier
      </Button>
    );
  };

  const headerUnitMinMax = index => {
    const isLastColumn = index === column.length - 1;

    const triggerRevalidation = field => {
      const currentValue = form.getFieldValue(field);
      form.setFieldsValue({ [field]: currentValue });
    };

    return (
      <Row gutter={16} style={{ width: '100%', paddingTop: '25px', position: 'relative', marginLeft: '0px' }}>
        <Col span={12}>
          <Form.Item key={`header[${index}].minUnit`}>
            {getFieldDecorator(`header[${index}].minUnit`, {
              rules: [
                { required: true, message: 'Please input minimum unit' },
                {
                  validator: (_, value, callback) => {
                    const maxUnit = form.getFieldValue(`header[${index}].maxUnit`);
                    if (value && maxUnit && value >= maxUnit) {
                      callback('Min must be less than Max');
                    } else if (index > 0) {
                      const prevMaxUnit = form.getFieldValue(`header[${index - 1}].maxUnit`);
                      if (prevMaxUnit !== undefined) {
                        if (value !== prevMaxUnit + 1) {
                          callback(`Min should be ${prevMaxUnit + 1}`);
                        } else {
                          callback();
                        }
                      } else {
                        callback();
                      }
                    } else {
                      callback();
                    }
                  }
                }
              ]
            })(
              <InputNumber
                placeholder="Min"
                style={{ width: '100%' }}
                min={1}
                step={1}
                precision={0}
                onChange={() => {
                  triggerRevalidation(`header[${index}].minUnit`);
                  if (index > 0) triggerRevalidation(`header[${index - 1}].maxUnit`);
                  triggerRevalidation(`header[${index}].maxUnit`);
                }}
              />
            )}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item key={`header[${index}].maxUnit`}>
            {getFieldDecorator(`header[${index}].maxUnit`, {
              rules: [
                { required: !isLastColumn, message: 'Please input maximum unit' },
                {
                  validator: (_, value, callback) => {
                    const minUnit = form.getFieldValue(`header[${index}].minUnit`);
                    if (value && minUnit && value <= minUnit) {
                      callback('Max must be greater than Min');
                    } else if (!isLastColumn) {
                      const nextMinUnit = form.getFieldValue(`header[${index + 1}].minUnit`);
                      if (nextMinUnit && value >= nextMinUnit) {
                        callback('Max must be less than next Min');
                      } else {
                        callback();
                      }
                    } else {
                      callback();
                    }
                  }
                }
              ]
            })(
              <InputNumber
                disabled={isLastColumn}
                placeholder="Max"
                style={{ width: '100%' }}
                min={1}
                step={1}
                precision={0}
                onChange={() => {
                  triggerRevalidation(`header[${index}].maxUnit`);
                  triggerRevalidation(`header[${index}].minUnit`);
                  if (!isLastColumn) triggerRevalidation(`header[${index + 1}].minUnit`);
                }}
              />
            )}
          </Form.Item>
        </Col>
        <Col span={24} style={{ position: 'absolute', top: '0' }}>
          <div style={{ textAlign: 'right' }}>
            {index !== 0 ? (
              <Button type="danger" size="small" style={{ height: '20px' }} onClick={() => handleRemoveBox(index)}>
                -
              </Button>
            ) : null}
          </div>
        </Col>
      </Row>
    );
  };

  return (
    <div className={styles.pricingGridContainer}>
      <Form.Item label="Pricing Table Name">
        {getFieldDecorator('pricingTableName', {
          rules: [{ required: true, message: 'Please input the name of the pricing table' }]
        })(<Input placeholder="Pricing Table Name" />)}
      </Form.Item>

      {/* Split layout: fixed left column + independently scrollable right table */}
      <div className={styles.tableWrapper}>
        {/* ── LEFT: fixed Package/Unit column, never scrolls ── */}
        <div className={styles.fixedCol}>
          <table className={styles.fixedTable}>
            <thead>
              <tr>
                <th className={styles.pricingGridHeader} style={{ width: LABEL_COL_WIDTH, padding: 0 }}>
                  <div className={styles.diagonalHeader}>
                    <span className={styles.productLabel}>Package</span>
                    <span className={styles.unitLabel}>Unit</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {row.map(productIndex => (
                <tr key={productIndex}>
                  <td className={styles.pricingGridItem} style={{ width: LABEL_COL_WIDTH }}>
                    <Form.Item style={{ width: '100%', textAlign: 'center', marginBottom: '0px' }}>
                      {getFieldDecorator(`product[${productIndex}][${0}]`, {
                        rules: [{ required: true, message: 'Please select a product' }]
                      })(
                        <Select
                          placeholder="Select a product"
                          style={{ width: '100%' }}
                          onChange={value => handleProductSelection(value, productIndex)}
                          labelInValue
                        >
                          {productData
                            .filter(
                              product =>
                                !selectedProducts.some(sp => sp.key === product._id && sp.fieldIndex !== productIndex) &&
                                product.categoryParent.includes('system')
                            )
                            .map(product => (
                              <Select.Option key={product._id} value={product._id}>
                                {product.name}
                              </Select.Option>
                            ))}
                        </Select>
                      )}
                    </Form.Item>
                    {productIndex !== 0 && (
                      <div style={{ position: 'absolute', top: '5px', right: '5px' }}>
                        <Button type="danger" size="small" style={{ height: '20px' }} onClick={() => handleRemoveRow(productIndex)}>
                          -
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {/* Add Product row */}
              {!(chargedByFunctionsOnly && row.length >= 1) && (
                <tr>
                  <td className={styles.pricingGridItem} style={{ width: LABEL_COL_WIDTH }}>
                    <Button onClick={addProduct} type="dashed" style={{ width: '100%' }}>
                      <Icon type="plus" /> Add Product
                    </Button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── RIGHT: scrollable tier columns ── */}
        <div className={styles.scrollableCol}>
          <table className={styles.scrollableTable}>
            <colgroup>
              {column.map((_, i) => (
                <col key={i} style={{ width: MIN_COL_WIDTH, minWidth: MIN_COL_WIDTH }} />
              ))}
              {/* Add Tier button column */}
              <col style={{ width: MIN_COL_WIDTH, minWidth: MIN_COL_WIDTH }} />
            </colgroup>
            <thead>
              <tr>
                {column.map((_, index) => (
                  <th key={index} className={styles.pricingGridHeader} style={{ minWidth: MIN_COL_WIDTH, width: MIN_COL_WIDTH, textAlign: 'center' }}>
                    {headerUnitMinMax(index)}
                  </th>
                ))}
                <th
                  className={`${styles.pricingGridHeader} ${styles.addTierCell}`}
                  style={{ minWidth: MIN_COL_WIDTH, width: MIN_COL_WIDTH, textAlign: 'center' }}
                >
                  {addTierButton()}
                </th>
              </tr>
            </thead>
            <tbody>
              {row.map(productIndex => (
                <tr key={productIndex}>
                  {column.map((_, colIndex) => (
                    <td
                      key={`pricing-${productIndex}-${colIndex}`}
                      className={styles.pricingGridItem}
                      style={{ minWidth: MIN_COL_WIDTH, width: MIN_COL_WIDTH }}
                    >
                      {pricing(colIndex, productIndex)}
                    </td>
                  ))}
                  {/* Empty cell under Add Tier header — always present */}
                  <td className={`${styles.pricingGridItem} ${styles.addTierCell}`} style={{ minWidth: MIN_COL_WIDTH, width: MIN_COL_WIDTH }} />
                </tr>
              ))}
              {/* Add Product row — empty cells to match left table */}
              {!(chargedByFunctionsOnly && row.length >= 1) && (
                <tr>
                  {column.map((_, i) => (
                    <td key={i} className={styles.pricingGridItem} style={{ minWidth: MIN_COL_WIDTH, width: MIN_COL_WIDTH }} />
                  ))}
                  {/* Empty cell under Add Tier header — always present */}
                  <td className={`${styles.pricingGridItem} ${styles.addTierCell}`} style={{ minWidth: MIN_COL_WIDTH, width: MIN_COL_WIDTH }} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Form.create()(PricingTableModal);
